import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, keys, useMe } from '../../api.ts';
import { formatDate, formatDateTime } from '../../../shared/format.ts';
import type { UserSummary } from '../../../shared/types.ts';
import { CallLink, ErrorBox, Help, Loading, PageHeader, useDocumentTitle } from '../../components/ui.tsx';

type SortKey = 'id' | 'name' | 'licenceDate' | 'updatedAt' | 'lastLogin' | 'qsoCount' | 'active';

export function UsersPage() {
  useDocumentTitle('Käyttäjät');
  const { data: me } = useMe();
  const q = useQuery({ queryKey: keys.users, queryFn: () => api.get<UserSummary[]>('/users') });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'id', dir: 1 });

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = (q.data ?? []).filter((u) => !s || u.id.toLowerCase().includes(s) || u.name.toLowerCase().includes(s));
    return [...list].sort((a, b) => {
      const av = a[sort.key] ?? '';
      const bv = b[sort.key] ?? '';
      return (av < bv ? -1 : av > bv ? 1 : 0) * sort.dir;
    });
  }, [q.data, search, sort]);

  const th = (key: SortKey, label: string, num = false) => (
    <th
      className={`sortable${num ? ' num' : ''}`}
      onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : key === 'id' || key === 'name' ? 1 : -1 }))}
      aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}
    >
      {label} {sort.key === key ? (sort.dir === 1 ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <div className="page">
      <PageHeader
        title="Käyttäjät"
        sub={q.data ? `${q.data.length} käyttäjää` : undefined}
        actions={
          <>
            <Help name="KayttajatLista" />
            {me?.isAdmin && (
              <Link className="btn btn-primary" to="/users/new">
                + Lisää käyttäjä
              </Link>
            )}
          </>
        }
      />
      <div className="row" style={{ marginBottom: 14 }}>
        <input
          className="input"
          style={{ maxWidth: 320 }}
          placeholder="Hae kutsulla tai nimellä…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Hae käyttäjää"
        />
      </div>
      <ErrorBox error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="table-wrap table-scroll">
          <table className="table">
            <thead>
              <tr>
                {th('id', 'Kutsu')}
                {th('name', 'Nimi')}
                {th('qsoCount', 'QSO:t', true)}
                {th('licenceDate', 'Lisenssi')}
                {th('updatedAt', 'Muokattu')}
                {me?.isAdmin && th('lastLogin', 'Kirjautunut')}
                {th('active', 'Tila')}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className={u.id === me?.id ? 'me' : undefined}>
                  <td>
                    <CallLink id={u.id} />
                  </td>
                  <td>{u.name}</td>
                  <td className="num">{u.qsoCount ? u.qsoCount.toLocaleString('fi-FI') : <span className="faint">–</span>}</td>
                  <td>{formatDate(u.licenceDate)}</td>
                  <td className="muted">{formatDateTime(u.updatedAt)}</td>
                  {me?.isAdmin && <td className="muted">{formatDateTime(u.lastLogin)}</td>}
                  <td>
                    {u.roleId === 1 && <span className="badge badge-accent">Ylläpitäjä</span>}{' '}
                    {!u.active && <span className="badge badge-danger">Passiivinen</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <div className="empty">Ei käyttäjiä hakuehdolla.</div>}
        </div>
      )}
    </div>
  );
}
